/**
 * Object Pool - Reuse objects to avoid GC
 *
 * Critical for 60fps - avoids allocation during gameplay
 * Use for: particles, effects, temporary vectors, etc.
 */

export interface Poolable {
  /** Reset object to initial state for reuse */
  reset(): void;
  /** Check if object is currently in use */
  isActive: boolean;
}

export interface PoolConfig<T> {
  /** Factory function to create new objects */
  create: () => T;
  /** Optional reset function (if T doesn't implement Poolable) */
  reset?: (obj: T) => void;
  /** Initial pool size (default: 16) */
  initialSize?: number;
  /** Maximum pool size (default: 256) */
  maxSize?: number;
  /** Auto-expand when pool is empty (default: true) */
  autoExpand?: boolean;
  /** Expansion batch size (default: 8) */
  expandSize?: number;
}

export class ObjectPool<T> {
  private pool: T[] = [];
  private active: Set<T> = new Set();
  private create: () => T;
  private resetFn?: (obj: T) => void;
  private maxSize: number;
  private autoExpand: boolean;
  private expandSize: number;

  constructor(config: PoolConfig<T>) {
    this.create = config.create;
    this.resetFn = config.reset;
    this.maxSize = config.maxSize ?? 256;
    this.autoExpand = config.autoExpand ?? true;
    this.expandSize = config.expandSize ?? 8;

    // Pre-populate pool
    const initialSize = config.initialSize ?? 16;
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(this.create());
    }
  }

  /**
   * Get an object from the pool
   * Returns null if pool is empty and auto-expand is disabled
   */
  acquire(): T | null {
    let obj: T | undefined;

    if (this.pool.length > 0) {
      obj = this.pool.pop()!;
    } else if (this.autoExpand && this.active.size < this.maxSize) {
      // Expand pool
      const expandCount = Math.min(this.expandSize, this.maxSize - this.active.size);
      for (let i = 0; i < expandCount; i++) {
        this.pool.push(this.create());
      }
      obj = this.pool.pop()!;
    }

    if (obj) {
      this.active.add(obj);
      return obj;
    }

    return null;
  }

  /**
   * Return an object to the pool
   */
  release(obj: T): void {
    if (!this.active.has(obj)) {
      console.warn('Attempting to release object not from this pool');
      return;
    }

    this.active.delete(obj);

    // Reset the object
    if (this.resetFn) {
      this.resetFn(obj);
    } else if (typeof (obj as Poolable).reset === 'function') {
      (obj as Poolable).reset();
    }

    this.pool.push(obj);
  }

  /**
   * Release all active objects
   */
  releaseAll(): void {
    for (const obj of this.active) {
      if (this.resetFn) {
        this.resetFn(obj);
      } else if (typeof (obj as Poolable).reset === 'function') {
        (obj as Poolable).reset();
      }
      this.pool.push(obj);
    }
    this.active.clear();
  }

  /**
   * Get pool statistics
   */
  get stats() {
    return {
      available: this.pool.length,
      active: this.active.size,
      total: this.pool.length + this.active.size,
      maxSize: this.maxSize,
    };
  }

  /**
   * Iterate over active objects
   */
  forEach(callback: (obj: T) => void): void {
    for (const obj of this.active) {
      callback(obj);
    }
  }

  /**
   * Clear the entire pool
   */
  clear(): void {
    this.pool.length = 0;
    this.active.clear();
  }
}

// ============================================================================
// VECTOR POOL (Common use case)
// ============================================================================

export interface Vector3Like {
  x: number;
  y: number;
  z: number;
}

class PooledVector3 implements Vector3Like, Poolable {
  x = 0;
  y = 0;
  z = 0;
  isActive = false;

  set(x: number, y: number, z: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  copy(v: Vector3Like): this {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }

  reset(): void {
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.isActive = false;
  }
}

// Pre-configured vector pool
export const vectorPool = new ObjectPool<PooledVector3>({
  create: () => new PooledVector3(),
  initialSize: 64,
  maxSize: 512,
});

// ============================================================================
// QUATERNION POOL
// ============================================================================

export interface QuaternionLike {
  x: number;
  y: number;
  z: number;
  w: number;
}

class PooledQuaternion implements QuaternionLike, Poolable {
  x = 0;
  y = 0;
  z = 0;
  w = 1;
  isActive = false;

  set(x: number, y: number, z: number, w: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }

  copy(q: QuaternionLike): this {
    this.x = q.x;
    this.y = q.y;
    this.z = q.z;
    this.w = q.w;
    return this;
  }

  reset(): void {
    this.x = 0;
    this.y = 0;
    this.z = 0;
    this.w = 1;
    this.isActive = false;
  }
}

export const quaternionPool = new ObjectPool<PooledQuaternion>({
  create: () => new PooledQuaternion(),
  initialSize: 32,
  maxSize: 256,
});
